import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Pressable, View } from "react-native"
import { useSelector } from "react-redux"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { hasFeature } from "@data/utility/Utils"
import TutorEventSnackbar from "./TutorEventSnackbar"
import TutorEventsBell from "./TutorEventsBell"
import TutorEventsDrawer from "./TutorEventsDrawer"
import { countActiveEvents, EVENT_CATALOG } from "./eventsCatalog"
import { useTutorEventsActions } from "./useTutorEventsActions"
import { useTutorEventsQueue } from "./useTutorEventsQueue"

// Gated by the live room's version_ft, like the other live features
// (checkpoint-rh…). Ops adds "events-panel" to a pilot room's
// version_ft to switch the panel on for that room — no per-user permission.
export const TUTOR_EVENTS_FEATURE = "events-panel"

const DEV_FORCE_ENABLED =
    __DEV__ && process.env.EXPO_PUBLIC_FORCE_TUTOR_EVENTS_PANEL === "true"

export const selectTutorEventsPanelEnabled = (state) => {
    if (DEV_FORCE_ENABLED) return true
    const liveSessionId = state.liveMode?.liveSessionId
    const liveRoom = state.data?.currentUser?.object?.live_sessions?.find(
        (s) => s?.id === liveSessionId,
    )?.live_room
    return hasFeature(liveRoom, TUTOR_EVENTS_FEATURE)
}

export const useTutorEventsPanelEnabled = () =>
    useSelector(selectTutorEventsPanelEnabled)

const BELL_SIZE = 44

const TutorEventsPanel = ({
    liveSessionId,
    students,
    currentUser,
    footerHeight = 70,
    onPinStudent,
}) => {
    const dimensions = useContext(DimensionsContext)
    const { events, markResolved } = useTutorEventsQueue({
        students,
    })
    const actions = useTutorEventsActions({
        liveSessionId,
        students,
        currentUser,
        onPinStudent,
    })

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [snackbarEvent, setSnackbarEvent] = useState(null)
    const [snackbarQueue, setSnackbarQueue] = useState([])
    const knownP1HashesRef = useRef(new Set())

    const notifyEvents = useMemo(
        () =>
            events.filter(
                (e) =>
                    EVENT_CATALOG[e.type]?.notify === true &&
                    e.cta?.kind !== "passive" &&
                    !e.isResolved,
            ),
        [events],
    )

    const activeCount = useMemo(() => countActiveEvents(events), [events])

    const hasP1 = useMemo(
        () =>
            events.some(
                (e) =>
                    EVENT_CATALOG[e.type]?.priority === 1 &&
                    e.cta?.kind !== "passive" &&
                    !e.isResolved,
            ),
        [events],
    )

    useEffect(() => {
        // Prune known hashes no longer in the active notify list so the ref
        // doesn't accumulate over a long session. Re-arrival is irrelevant —
        // an event that disappears and comes back gets a new startedAt (and
        // therefore a new hash), so the dedup is per event-lifetime anyway.
        const activeHashes = new Set(notifyEvents.map((e) => e.hash))
        knownP1HashesRef.current.forEach((h) => {
            if (!activeHashes.has(h)) knownP1HashesRef.current.delete(h)
        })

        const incoming = notifyEvents.filter(
            (e) => !knownP1HashesRef.current.has(e.hash),
        )
        if (incoming.length === 0) return
        incoming.forEach((e) => knownP1HashesRef.current.add(e.hash))
        setSnackbarQueue((prev) => [...prev, ...incoming])
    }, [notifyEvents])

    useEffect(() => {
        if (!snackbarEvent && snackbarQueue.length > 0) {
            const [next, ...rest] = snackbarQueue
            setSnackbarEvent(next)
            setSnackbarQueue(rest)
        }
    }, [snackbarEvent, snackbarQueue])

    useEffect(() => {
        if (!snackbarEvent) return
        const current = events.find((e) => e.hash === snackbarEvent.hash)
        if (!current || current.isResolved) {
            setSnackbarEvent(null)
        }
    }, [events, snackbarEvent])

    // Drop still-queued (not yet shown) snackbars whose event resolved or
    // disappeared, so they don't pop up later with a stale snapshot.
    useEffect(() => {
        setSnackbarQueue((prev) => {
            const next = prev.filter((q) => {
                const current = events.find((e) => e.hash === q.hash)
                return current && !current.isResolved
            })
            return next.length === prev.length ? prev : next
        })
    }, [events])

    const handlePrimary = (event) => {
        actions.runEventAction(event, { markResolved })
        if (snackbarEvent?.hash === event.hash) {
            setSnackbarEvent(null)
        }
    }

    const handleDismiss = (event) => {
        actions.dismissEvent(event)
        if (snackbarEvent?.hash === event.hash) {
            setSnackbarEvent(null)
        }
    }

    // Compromis spec : drawer plus étroit que TutorGridLayoutM (qui prend
    // toute la largeur visio+tiles). 32% de la fenêtre, capé entre 300 et
    // 380 px pour rester lisible sans écraser la grille élèves derrière.
    const drawerWidth = Math.min(
        380,
        Math.max(300, Math.floor((dimensions?.width ?? 800) * 0.32)),
    )

    const bellBottom = Math.max(8, Math.floor((footerHeight - BELL_SIZE) / 2))
    const bellRight = 18

    const overlayBottom = footerHeight + 12
    const overlayRight = 16

    return (
        <View
            pointerEvents="box-none"
            style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 30,
            }}
        >
            {drawerOpen && (
                <Pressable
                    onPress={() => setDrawerOpen(false)}
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 20,
                    }}
                />
            )}
            <TutorEventsBell
                activeCount={activeCount}
                hasP1={hasP1}
                onPress={() => setDrawerOpen((v) => !v)}
                style={{
                    position: "absolute",
                    bottom: bellBottom,
                    right: bellRight,
                    zIndex: 50,
                }}
            />
            <TutorEventsDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                events={events}
                onPrimaryAction={handlePrimary}
                onDismiss={handleDismiss}
                width={drawerWidth}
                bottom={overlayBottom}
                right={overlayRight}
            />
            <TutorEventSnackbar
                event={snackbarEvent}
                onClose={() => setSnackbarEvent(null)}
                onPrimaryAction={handlePrimary}
                bottom={overlayBottom}
                right={overlayRight}
            />
        </View>
    )
}

export default TutorEventsPanel
